/* eslint-disable no-console */
import type { PageTypes } from "@lib/enums/PageTypes";
import { hasWindow } from "./../utils/hasWindow";
import { getDatadogLogs } from "./datadogHelper";
import { createErrorParser } from "./utils/createErrorParser";

export enum LogType {
    INFO = "info",
    WARN = "warn",
    ERROR = "error",
}

export enum ServiceType {
    CONTENTFUL = "Contentful",
    COMMERCE_TOOLS = "CommerceTools",
    NEXT_API = "Next.js API",
    ALGOLIA = "Algolia",
    WEB = "Web",
    ADYEN = "Adyen",
    YOTPO = "Yotpo",
    AMAZON_PAY = "AmazonPay",
    GOOGLE = "Google",
    RECAPTCHA = "Recaptcha",
    MICROSOFT = "Microsoft",
    AUDIO_EYE = "AudioEye",
    RAFFLE = "Raffle",
}

export enum LogTag {
    PAYMENT = "PAYMENT",
    ORDER = "ORDER",
    MICROCOPY = "MICROCOPY",
    METADATA = "METADATA",
    PERFORMANCE = "PERFORMANCE",
    ERROR = "ERROR",
    SUCCESS = "SUCCESS",
    MISSING_ADDRESS_FIELDS = "MISSING_ADDRESS_FIELDS",
    MISSING_IMAGE = "MISSING_IMAGE",
    DISCOUNTS = "DISCOUNTS",
    STORES = "STORES",
    PRODUCT = "PRODUCT",
    FEED = "FEED",
    ANALYTICS = "ANALYTICS",
    ACCOUNT = "ACCOUNT",
    CHECKOUT = "CHECKOUT",
    ACCESSIBILITY = "ACCESSIBILITY",
    REVIEW = "REVIEW",
    REVALIDATION = "REVALIDATION",
    RECAPTCHA = "RECAPTCHA",
    ORDER_HISTORY = "ORDER_HISTORY",
    ADDRESS_LOOKUP = "ADDRESS_LOOKUP",
    AUTHENTICATION = "AUTHENTICATION",
    LIVE_CHAT = "LIVE_CHAT",
    CONTACT_FORM = "CONTACT_FORM",
    EXPRESS_CHECKOUT = "EXPRESS_CHECKOUT",
    SHIPPING = "SHIPPING",
    SHOPPING_BAG = "SHOPPING_BAG",
    LOYALTY_HISTORY = "LOYALTY_HISTORY",
    FEATURE_FLAGS = "FEATURE_FLAGS",
    HEAP_API = "HEAP_API",
    BACK_IN_STOCK_NOTIFICATION = "BACK_IN_STOCK_NOTIFICATION",
    SECURITY_REPORTING = "SECURITY_REPORTING",
    ALGOLIA_RECOMMENDATION = "ALGOLIA_RECOMMENDATION",
    SOCIAL_LOGIN = "SOCIAL_LOGIN",
    RAFFLE = "RAFFLE",
}

type LogPayload = {
    [key: string]: any;
    tag: LogTag;
    _duration?: number;
    _pageType?: PageTypes;
};

const consoleLogFunctions = {
    [LogType.ERROR]: console.error,
    [LogType.WARN]: console.warn,
    [LogType.INFO]: console.log,
};

export abstract class Logger {
    private static log(
        logType: LogType,
        serviceType: ServiceType,
        message: string,
        payload: LogPayload
    ) {
        const { tag, ...restPayload } = payload;
        const logPayload = {
            _logger: "ECCO",
            _tag: payload.tag,
            _level: logType,
            _service: serviceType,
            _message: message,
            _caller: hasWindow ? "client" : "server",
            _data: restPayload,
        };

        if (hasWindow) {
            if (process.env.NEXT_PUBLIC_LOGGING) {
                consoleLogFunctions[logType](message, logPayload);
            }

            // Use global DD_LOGS to avoid including @datadog/browser-logs in every bundle
            // Datadog is initialized in ClientProviders
            getDatadogLogs()?.logger[logType](message, logPayload);
        } else {
            const stringifiedPayload = JSON.stringify(logPayload);

            if (process.env.NODE_ENV === "production" || process.env.NEXT_PUBLIC_LOGGING) {
                consoleLogFunctions[logType](stringifiedPayload);
            }
        }
    }

    static info(serviceType: ServiceType, message: string, payload: LogPayload) {
        this.log(LogType.INFO, serviceType, message, payload);
    }

    static warn(serviceType: ServiceType, message: string, payload: LogPayload) {
        const errorParser = createErrorParser();
        const { tag, _duration, _pageType, ...restPayload } = payload;
        this.log(LogType.WARN, serviceType, message, {
            tag,
            _duration,
            _pageType,
            ...errorParser(restPayload ?? {}),
        });
    }

    static error(serviceType: ServiceType, message: string, payload: LogPayload) {
        const errorParser = createErrorParser();
        const { tag, _duration, _pageType, ...restPayload } = payload;
        this.log(LogType.ERROR, serviceType, message, {
            tag,
            _duration,
            _pageType,
            ...errorParser(restPayload ?? {}),
        });
    }
}
